import React, { useState } from "react";
import { X, Plus, Minus, Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CartDrawer = () => {
  const { isOpen, closeCart, items, removeItem, setQty, subtotal, count } = useCart();
  const { token } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");

  const checkout = async () => {
    setCheckingOut(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          items: items.map((i) => ({ lookup_key: i.id, quantity: i.qty })),
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setError(data.detail || "Checkout failed — please try again.");
    } catch {
      setError("Checkout failed — please try again.");
    }
    setCheckingOut(false);
  };

  return (
    <>
      <div
        className={`cart-scrim ${isOpen ? "is-open" : ""}`}
        onClick={closeCart}
        aria-hidden={!isOpen}
      />
      <aside
        className={`cart-drawer ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingBag size={20} />
            <span>Cart ({count})</span>
          </div>
          <button className="cart-close" onClick={closeCart} aria-label="Close cart">
            <X size={20} />
          </button>
        </div>

        <div className="cart-body">
          {items.length === 0 ? (
            <div className="cart-empty">
              <p>Your cart is empty.</p>
              <p className="cart-empty-sub">Head to the shop to add something.</p>
            </div>
          ) : (
            items.map((item) => (
              <div className="cart-line" key={item.id}>
                <img src={item.image} alt={item.name} className="cart-line-img" />
                <div className="cart-line-info">
                  <div className="cart-line-name">{item.name}</div>
                  <div className="cart-line-price">${item.price.toFixed(2)}</div>
                  <div className="cart-line-qty">
                    <button
                      onClick={() => setQty(item.id, item.qty - 1)}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={14} />
                    </button>
                    <span>{item.qty}</span>
                    <button
                      onClick={() => setQty(item.id, item.qty + 1)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="cart-line-remove"
                      onClick={() => removeItem(item.id)}
                      aria-label="Remove item"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="cart-line-sub">
                  ${(item.price * item.qty).toFixed(2)}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="cart-footer">
          <div className="cart-subtotal">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {error && (
            <div className="cart-error" data-testid="cart-checkout-error">{error}</div>
          )}
          <button
            className="cart-checkout"
            disabled={items.length === 0 || checkingOut}
            onClick={checkout}
            data-testid="cart-checkout-btn"
          >
            {checkingOut ? (
              <>
                <Loader2 size={16} className="payment-spin" /> Redirecting…
              </>
            ) : (
              "Checkout"
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
